import { baseApi } from 'api'
import { ApiResponse } from 'helpers/types'
import { TContact } from 'modules/contact/types'
import {
  ContactListResponse,
  CustomerCreateRequest,
  CustomerItemResponse,
  CustomerListResponse,
  SuggestionAddressItem,
  SuggestionAddressResponse,
  SuggestionCompanyItem,
  SuggestionCompanyResponse
} from 'schema'

import { TCustomer } from '../types'

export type TCustomerIpCreateRequestData = CustomerCreateRequest

export type TCustomerIpCreateResponseData = CustomerItemResponse

export type TAddress = SuggestionAddressItem

export const COMPANY_SUGGESTION_ERROR = 'Не найдено, уточните запрос'
export const ADDRESS_SUGGESTION_ERROR = 'Не найдено, уточните запрос'

export const customerApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getCustomerList: build.query<TCustomer[], void>({
      query: () => ({
        url: 'customer',
        method: 'GET'
      }),
      transformResponse: (response: ApiResponse<CustomerListResponse>) => response.data.customer,
      providesTags: ['CUSTOMER_LIST']
    }),
    getCustomerById: build.query<TCustomer, string>({
      query: (id) => ({
        url: `customer/${id}`,
        method: 'GET'
      }),
      transformResponse: (response: ApiResponse<CustomerItemResponse>) => response.data.customer
    }),
    deleteCustomer: build.mutation<string, string>({
      query: (id) => ({
        url: `customer/${id}`,
        method: 'DELETE'
      })
    }),
    getCompany: build.mutation<SuggestionCompanyItem[] | null, string>({
      query: (search) => ({
        url: `suggestion/company?query=${search}`,
        method: 'GET'
      }),

      transformResponse: (response: ApiResponse<SuggestionCompanyResponse | null>) => {
        const { company } = response?.data || {}
        if (Array.isArray(company) && !company?.length) {
          throw Error(COMPANY_SUGGESTION_ERROR)
        }

        return company || null
      }
    }),
    getAddress: build.mutation<SuggestionAddressItem[] | null, string>({
      query: (search) => ({
        url: `suggestion/address?query=${search}`,
        method: 'GET'
      }),
      transformResponse: (response: ApiResponse<SuggestionAddressResponse | null>) => {
        // @ts-ignore
        const { address } = response?.data || {}
        if (Array.isArray(address) && !address?.length) {
          throw Error(ADDRESS_SUGGESTION_ERROR)
        }

        return address || null
      }
    }),
    addCustomer: build.mutation<TCustomer, TCustomerIpCreateRequestData>({
      query: (body) => ({
        url: 'customer',
        method: 'POST',
        body
      }),
      transformResponse: (response: ApiResponse<TCustomerIpCreateResponseData>) => response.data.customer
    }),
    getContactsByCustomerId: build.query<TContact[], string>({
      query: (id) => ({
        url: `customer/${id}/contact`,
        method: 'GET'
      }),
      transformResponse: (response: ApiResponse<ContactListResponse>) => response.data.contact
    }),
    attachContactByCustomerId: build.mutation<string, { customerId: TCustomer['id']; id: TContact['id'] }>({
      query: ({ customerId, id }) => ({
        url: `customer/${customerId}/contact/${id}`,
        method: 'POST'
      })
    }),
    detachContactByCustomerId: build.mutation<string, { customerId: TCustomer['id']; id: TContact['id'] }>({
      query: ({ customerId, id }) => ({
        url: `customer/${customerId}/contact/${id}`,
        method: 'DELETE'
      })
    }),
    findAllRecommendedCustomer: build.query<TCustomer[], void>({
      query: () => ({
        url: 'customer/recommended',
        method: 'GET'
      }),
      transformResponse: (response: ApiResponse<CustomerListResponse>) => response.data.customer
    })
  }),
  overrideExisting: false
})

export const {
  useGetCustomerListQuery,
  useLazyGetCustomerListQuery,
  useDeleteCustomerMutation,
  useGetCustomerByIdQuery,
  useGetCompanyMutation,
  useGetAddressMutation,
  useAddCustomerMutation,
  useGetContactsByCustomerIdQuery,
  useLazyGetContactsByCustomerIdQuery,
  useAttachContactByCustomerIdMutation,
  useDetachContactByCustomerIdMutation,
  useFindAllRecommendedCustomerQuery
} = customerApi
